import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { clientAPI, caseAPI, visaProgramAPI, b2bPartnerAPI, userAPI } from '../utils/api';
import { Plus, Users, Briefcase, AlertTriangle, Copy, Check, Key, X, Mail, Edit3, Save, Loader2, UserCheck, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export default function AdminClients() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [b2bPartners, setB2bPartners] = useState([]);
  const [officers, setOfficers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [newClientCredentials, setNewClientCredentials] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  // Edit client state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  // Reset portal password
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetCredentials, setResetCredentials] = useState(null);
  const [resettingPassword, setResettingPassword] = useState(false);

  // Case assignment state (inside edit modal)
  const [clientCase, setClientCase] = useState(null);
  const [caseLoading, setCaseLoading] = useState(false);
  const [assigningCase, setAssigningCase] = useState(false);
  const [selectedOfficer, setSelectedOfficer] = useState('');

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    passport_number: '',
    nationality: '',
    country_applying_to: '',
    visa_type: 'Visitor Visa',
    payment_amount: '',
    payment_method: 'mock',
    payment_status: 'full_paid',
    balance_due_date: '',
    sales_consultant: '',
    customer_type: 'InHouse',
    b2b_partner_id: '',
    assigned_officer_id: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [clientsRes, programsRes, partnersRes, usersRes] = await Promise.all([
        clientAPI.getClients(),
        visaProgramAPI.getPrograms(),
        b2bPartnerAPI.getPartners(),
        userAPI.getUsers()
      ]);
      setClients(clientsRes.data);
      setPrograms(programsRes.data);
      setB2bPartners(partnersRes.data || []);
      setOfficers((usersRes.data || []).filter(u => u.role === 'case_officer'));
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (client) => {
    setResettingPassword(true);
    try {
      const res = await clientAPI.resetPortalPassword(client.id);
      setResetCredentials({ ...res.data, clientName: res.data.client_name });
      setShowResetModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  const fetchClientCase = async (clientId) => {
    setCaseLoading(true);
    try {
      const res = await caseAPI.getCases();
      const cases = res.data || [];
      const found = cases.find(c => c.client_id === clientId);
      setClientCase(found || null);
      setSelectedOfficer(found?.assigned_officer_id || '');
    } catch {
      setClientCase(null);
    } finally {
      setCaseLoading(false);
    }
  };

  const handleAssignOrCreateCase = async () => {
    if (!editingClient) return;
    setAssigningCase(true);
    try {
      if (!clientCase) {
        // Create case + optionally assign officer
        const res = await clientAPI.createCaseForClient(editingClient.id, {
          assigned_officer_id: selectedOfficer || null
        });
        toast.success(`Case ${res.data.ucin} created${selectedOfficer ? ' and officer assigned' : ''}! Notifications sent.`);
        setClientCase(res.data.case);
      } else {
        // Just assign/reassign officer on existing case
        await caseAPI.updateCase(clientCase.id, { assigned_officer_id: selectedOfficer });
        toast.success('Officer assigned! Email and notification sent.');
        setClientCase({ ...clientCase, assigned_officer_id: selectedOfficer });
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign case');
    } finally {
      setAssigningCase(false);
    }
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success(`${field} copied to clipboard`);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  // Edit client functions
  const openEditModal = (client) => {
    setEditingClient(client);
    setEditFormData({
      full_name: client.full_name || '',
      email: client.email || '',
      phone: client.phone || '',
      passport_number: client.passport_number || '',
      nationality: client.nationality || '',
      country_applying_to: client.country_applying_to || '',
      visa_type: client.visa_type || 'Visitor Visa',
      payment_amount: client.payment_amount || '',
      payment_method: client.payment_method || 'mock',
      payment_status: client.payment_status || 'full_paid',
      balance_due_date: client.balance_due_date ? client.balance_due_date.split('T')[0] : '',
      sales_consultant: client.sales_consultant || '',
      customer_type: client.customer_type || 'InHouse',
      b2b_partner_name: client.b2b_partner_name || ''
    });
    setClientCase(null);
    setSelectedOfficer('');
    fetchClientCase(client.id);
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    // Validate balance due date if partial payment
    if (editFormData.payment_status === 'partial_paid' && !editFormData.balance_due_date) {
      toast.error('Balance due date is required for partial payment');
      return;
    }
    
    setSavingEdit(true);
    try {
      const updatePayload = {
        ...editFormData,
        payment_amount: parseFloat(editFormData.payment_amount),
        balance_due_date: editFormData.payment_status === 'partial_paid' ? editFormData.balance_due_date : null
      };
      
      await clientAPI.updateClient(editingClient.id, updatePayload);
      toast.success('Client updated successfully! Changes synced across all modules.');
      setShowEditModal(false);
      setEditingClient(null);
      fetchData(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update client');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate balance due date if partial payment
    if (formData.payment_status === 'partial_paid' && !formData.balance_due_date) {
      toast.error('Balance due date is required for partial payment');
      return;
    }
    
    try {
      const clientRes = await clientAPI.createClient({
        ...formData,
        payment_amount: parseFloat(formData.payment_amount),
        balance_due_date: formData.payment_status === 'partial_paid' ? formData.balance_due_date : null,
        b2b_partner_name: formData.customer_type === 'B2B' && formData.b2b_partner_id 
          ? b2bPartners.find(p => p.id === formData.b2b_partner_id)?.name 
          : null
      });
      
      // Extract client data and credentials from response
      const clientData = clientRes.data.client || clientRes.data;
      const clientId = clientData.id;
      const clientCredentials = clientRes.data.client_credentials;
      
      // Create case for the client
      const matchingProgram = programs.find(
        p => p.visa_type === formData.visa_type && p.country === formData.country_applying_to
      );
      
      let caseUcin = null;
      if (matchingProgram) {
        // Determine service validity based on visa type
        let serviceValidity = 1; // default 1 month
        if (formData.visa_type === 'Permanent Residency') {
          serviceValidity = 6; // 6 months for PR
        } else if (formData.visa_type === 'Student Visa') {
          serviceValidity = 3; // 3 months for student visa
        }
        
        const caseResponse = await caseAPI.createCase({
          client_id: clientId,
          visa_program_id: matchingProgram.id,
          service_validity_months: serviceValidity,
          client_type: formData.customer_type === 'B2B' ? 'b2b' : 'inhouse',
          b2b_partner_id: formData.customer_type === 'B2B' ? formData.b2b_partner_id : null,
          assigned_officer_id: formData.assigned_officer_id || null
        });
        caseUcin = caseResponse.data.ucin;
      }
      
      toast.success('Client created successfully!');
      setShowForm(false);
      fetchData();
      
      // Show credentials modal if new credentials were generated
      if (clientCredentials) {
        setNewClientCredentials({
          ...clientCredentials,
          ucin: caseUcin || 'N/A',
          clientName: formData.full_name
        });
        setShowCredentialsModal(true);
      }
      
      setFormData({
        full_name: '',
        email: '',
        phone: '',
        passport_number: '',
        nationality: '',
        country_applying_to: '',
        visa_type: 'Visitor Visa',
        payment_amount: '',
        payment_method: 'mock',
        payment_status: 'full_paid',
        balance_due_date: '',
        sales_consultant: '',
        customer_type: 'InHouse',
        b2b_partner_id: '',
        assigned_officer_id: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create client');
    }
  };

  const getPaymentStatusDisplay = (status) => {
    if (status === 'full_paid') return { label: 'Fully Paid', class: 'status-approved' };
    if (status === 'partial_paid') return { label: 'Partial Payment', class: 'bg-amber-100 text-amber-700 border-amber-200' };
    // Legacy support for old status values
    if (status === 'completed') return { label: 'Fully Paid', class: 'status-approved' };
    return { label: status, class: 'status-pending' };
  };

  const visaTypes = ['Visitor Visa', 'Student Visa', 'Dependent Visa', 'Investor Visa', 'Startup Visa', 'Permanent Residency'];
  const countries = ['Canada', 'Australia', 'New Zealand', 'United States', 'United Kingdom', 'Europe (Schengen)'];
  const salesConsultants = ['Zeeshan', 'Sadia', 'Amreen', 'Basheer', 'Syed'];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="main-content" data-testid="admin-clients-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2" data-testid="clients-title">Clients</h1>
            <p className="text-slate-600">Manage all your clients</p>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            data-testid="add-client-button"
            className="btn-primary flex items-center gap-2"
          >
            <Plus size={20} />
            Add Client
          </button>
        </div>

        {showForm && (
          <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6" data-testid="client-form">
            <h2 className="text-xl font-bold text-slate-900 mb-4">New Client</h2>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Full Name *</label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                  data-testid="input-full-name"
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  data-testid="input-email"
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Phone *</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  data-testid="input-phone"
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Passport Number <span className="text-slate-400">(Optional)</span></label>
                <input
                  type="text"
                  value={formData.passport_number}
                  onChange={(e) => setFormData({...formData, passport_number: e.target.value})}
                  data-testid="input-passport"
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                  placeholder="Can be added later"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Nationality *</label>
                <input
                  type="text"
                  value={formData.nationality}
                  onChange={(e) => setFormData({...formData, nationality: e.target.value})}
                  data-testid="input-nationality"
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Country Applying To *</label>
                <select
                  value={formData.country_applying_to}
                  onChange={(e) => setFormData({...formData, country_applying_to: e.target.value})}
                  data-testid="select-country"
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                  required
                >
                  <option value="">Select Country</option>
                  {countries.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Visa Type *</label>
                <select
                  value={formData.visa_type}
                  onChange={(e) => setFormData({...formData, visa_type: e.target.value})}
                  data-testid="select-visa-type"
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                  required
                >
                  {visaTypes.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Service Fee (₹) *</label>
                <input
                  type="number"
                  value={formData.payment_amount}
                  onChange={(e) => setFormData({...formData, payment_amount: e.target.value})}
                  data-testid="input-fee"
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                  required
                />
              </div>
              
              {/* Payment Status Section */}
              <div className="md:col-span-2 border-t border-slate-200 pt-4 mt-2">
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Payment Information</h3>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Payment Status *</label>
                <select
                  value={formData.payment_status}
                  onChange={(e) => setFormData({...formData, payment_status: e.target.value})}
                  data-testid="select-payment-status"
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                  required
                >
                  <option value="full_paid">Full Amount Paid</option>
                  <option value="partial_paid">Partial Payment</option>
                </select>
              </div>
              {formData.payment_status === 'partial_paid' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Balance Payment Due Date *</label>
                  <input
                    type="date"
                    value={formData.balance_due_date}
                    onChange={(e) => setFormData({...formData, balance_due_date: e.target.value})}
                    data-testid="input-balance-due-date"
                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                    min={new Date().toISOString().split('T')[0]}
                    required
                  />
                  <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                    <AlertTriangle size={12} />
                    Reminder emails will be sent 7, 3, and 1 day before the due date
                  </p>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Sales Consultant *</label>
                <select
                  value={formData.sales_consultant}
                  onChange={(e) => setFormData({...formData, sales_consultant: e.target.value})}
                  data-testid="select-sales-consultant"
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                  required
                >
                  <option value="">Select Sales Consultant</option>
                  {salesConsultants.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Assign Case Officer <span className="text-slate-400">(Optional — can assign later)</span>
                </label>
                <select
                  value={formData.assigned_officer_id}
                  onChange={(e) => setFormData({...formData, assigned_officer_id: e.target.value})}
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                >
                  <option value="">Select Officer (assign later)</option>
                  {officers.map(o => <option key={o.id} value={o.id}>{o.full_name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Customer Type *</label>
                <select
                  value={formData.customer_type}
                  onChange={(e) => setFormData({...formData, customer_type: e.target.value})}
                  data-testid="select-customer-type"
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                  required
                >
                  <option value="InHouse">InHouse (Marketing)</option>
                  <option value="B2B">B2B (Partner Referral)</option>
                </select>
              </div>
              {formData.customer_type === 'B2B' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">B2B Partner *</label>
                  <select
                    value={formData.b2b_partner_id}
                    onChange={(e) => setFormData({...formData, b2b_partner_id: e.target.value})}
                    data-testid="select-b2b-partner"
                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                    required
                  >
                    <option value="">Select B2B Partner</option>
                    {b2bPartners.map(partner => (
                      <option key={partner.id} value={partner.id}>{partner.name}</option>
                    ))}
                  </select>
                  {b2bPartners.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                      <AlertTriangle size={12} />
                      No B2B partners found. Please add partners first.
                    </p>
                  )}
                </div>
              )}
              <div className="md:col-span-2 flex gap-4">
                <button type="submit" data-testid="submit-client-button" className="btn-primary">
                  Create Client & Start Case
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  data-testid="cancel-button"
                  className="px-6 py-3 bg-slate-200 text-slate-700 rounded-lg font-semibold hover:bg-slate-300 transition-all"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="clients-table">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Name</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Email</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Visa Type</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Country</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Sales Consultant</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Fee</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Payment Status</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-slate-900">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {clients.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center text-slate-500">
                      No clients yet. Add your first client to get started.
                    </td>
                  </tr>
                ) : (
                  clients.map((client) => {
                    const paymentStatus = getPaymentStatusDisplay(client.payment_status);
                    return (
                      <tr 
                        key={client.id} 
                        className={`hover:bg-slate-50 transition-all ${client.is_suspended ? 'bg-orange-50' : ''}`} 
                        data-testid={`client-row-${client.id}`}
                      >
                        <td className="px-6 py-4 text-sm font-medium text-slate-900">
                          <div className="flex items-center gap-2">
                            {client.full_name}
                            {client.is_suspended && (
                              <span className="text-xs px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full">Suspended</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">{client.email}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{client.visa_type}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{client.country_applying_to}</td>
                        <td className="px-6 py-4 text-sm text-slate-700 font-medium">{client.sales_consultant || 'N/A'}</td>
                        <td className="px-6 py-4 text-sm text-slate-900 font-semibold">₹{client.payment_amount?.toLocaleString('en-IN')}</td>
                        <td className="px-6 py-4">
                          <div>
                            <span className={`status-badge ${paymentStatus.class}`}>{paymentStatus.label}</span>
                            {client.payment_status === 'partial_paid' && client.balance_due_date && (
                              <p className="text-xs text-slate-500 mt-1">
                                Due: {new Date(client.balance_due_date).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => navigate(`/admin/clients/${client.id}`)}
                              className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg transition-colors"
                              title="View Profile"
                            >
                              <Eye size={18} />
                            </button>
                            <button
                              onClick={() => openEditModal(client)}
                              className="p-2 text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                              title="Edit Client"
                              data-testid={`edit-client-${client.id}`}
                            >
                              <Edit3 size={18} />
                            </button>
                            <button
                              onClick={() => handleResetPassword(client)}
                              disabled={resettingPassword}
                              className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors disabled:opacity-50"
                              title="Reset Portal Password"
                            >
                              <Key size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Client Credentials Modal */}
        {showCredentialsModal && newClientCredentials && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="credentials-modal">
            <div className="bg-white rounded-xl w-full max-w-md mx-4 shadow-2xl">
              <div className="p-6 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                      <Key className="text-teal-600" size={24} />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-slate-900">Client Login Created</h2>
                      <p className="text-sm text-slate-500">Case {newClientCredentials.ucin}</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => {
                      setShowCredentialsModal(false);
                      setNewClientCredentials(null);
                    }} 
                    className="p-2 hover:bg-slate-100 rounded-lg"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-4">
                <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                  <p className="text-sm text-teal-800 font-medium mb-1">
                    New login credentials for {newClientCredentials.clientName}
                  </p>
                  <p className="text-xs text-teal-600">
                    Please share these credentials with the client. They will use this to access their portal.
                  </p>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Email / Username</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={newClientCredentials.email}
                        readOnly
                        className="flex-1 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 font-mono text-sm"
                        data-testid="credential-email"
                      />
                      <button
                        onClick={() => copyToClipboard(newClientCredentials.email, 'Email')}
                        className={`p-2.5 rounded-lg transition-all ${
                          copiedField === 'Email' 
                            ? 'bg-teal-500 text-white' 
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                        data-testid="copy-email-btn"
                      >
                        {copiedField === 'Email' ? <Check size={18} /> : <Copy size={18} />}
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Temporary Password</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={newClientCredentials.password}
                        readOnly
                        className="flex-1 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 font-mono text-sm"
                        data-testid="credential-password"
                      />
                      <button
                        onClick={() => copyToClipboard(newClientCredentials.password, 'Password')}
                        className={`p-2.5 rounded-lg transition-all ${
                          copiedField === 'Password' 
                            ? 'bg-teal-500 text-white' 
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                        data-testid="copy-password-btn"
                      >
                        {copiedField === 'Password' ? <Check size={18} /> : <Copy size={18} />}
                      </button>
                    </div>
                  </div>
                </div>
                
                <div className="pt-4 border-t border-slate-200 space-y-3">
                  <button
                    onClick={() => {
                      copyToClipboard(
                        `Login URL: ${window.location.origin}/login\nEmail: ${newClientCredentials.email}\nPassword: ${newClientCredentials.password}`,
                        'All Credentials'
                      );
                    }}
                    className="w-full px-4 py-2.5 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-all flex items-center justify-center gap-2"
                    data-testid="copy-all-btn"
                  >
                    {copiedField === 'All Credentials' ? (
                      <>
                        <Check size={18} />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy size={18} />
                        Copy All Credentials
                      </>
                    )}
                  </button>
                  
                  <a
                    href={`mailto:${newClientCredentials.email}?subject=${encodeURIComponent(`Your Immiways Portal Login Credentials - Case ${newClientCredentials.ucin}`)}&body=${encodeURIComponent(
`Dear ${newClientCredentials.clientName},

Welcome to Immiways Migration Advisory Services!

Your case (${newClientCredentials.ucin}) has been created. Please use the following credentials to access your client portal:

Login URL: ${window.location.origin}/login

Email: ${newClientCredentials.email}
Password: ${newClientCredentials.password}

Important: Please change your password after your first login for security purposes.

If you have any questions, please don't hesitate to contact us.

Best regards,
Immiways Team
`)}`}
                    className="w-full px-4 py-2.5 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 transition-all flex items-center justify-center gap-2"
                    data-testid="share-credentials-btn"
                  >
                    <Mail size={18} />
                    Share via Email
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Reset Portal Password Modal */}
        {showResetModal && resetCredentials && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl w-full max-w-md mx-4 shadow-2xl">
              <div className="p-6 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                      <Key className="text-amber-600" size={24} />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-slate-900">New Portal Password</h2>
                      <p className="text-sm text-slate-500">{resetCredentials.clientName}</p>
                    </div>
                  </div>
                  <button onClick={() => { setShowResetModal(false); setResetCredentials(null); }} className="p-2 hover:bg-slate-100 rounded-lg">
                    <X size={20} />
                  </button>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-800 font-medium">Password reset successfully. Share these new credentials with the client.</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Email / Username</label>
                  <div className="flex items-center gap-2">
                    <input type="text" value={resetCredentials.email} readOnly className="flex-1 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 font-mono text-sm" />
                    <button onClick={() => copyToClipboard(resetCredentials.email, 'Email')} className={`p-2.5 rounded-lg transition-all ${copiedField === 'Email' ? 'bg-teal-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                      {copiedField === 'Email' ? <Check size={18} /> : <Copy size={18} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">New Password</label>
                  <div className="flex items-center gap-2">
                    <input type="text" value={resetCredentials.password} readOnly className="flex-1 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 font-mono text-sm" />
                    <button onClick={() => copyToClipboard(resetCredentials.password, 'Password')} className={`p-2.5 rounded-lg transition-all ${copiedField === 'Password' ? 'bg-teal-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                      {copiedField === 'Password' ? <Check size={18} /> : <Copy size={18} />}
                    </button>
                  </div>
                </div>
                <div className="pt-4 border-t border-slate-200 flex gap-3">
                  <button
                    onClick={() => copyToClipboard(`Email: ${resetCredentials.email}\nPassword: ${resetCredentials.password}`, 'All Credentials')}
                    className="flex-1 px-4 py-2.5 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-all flex items-center justify-center gap-2"
                  >
                    {copiedField === 'All Credentials' ? <><Check size={18} />Copied!</> : <><Copy size={18} />Copy Both</>}
                  </button>
                  <a
                    href={`mailto:${resetCredentials.email}?subject=${encodeURIComponent('Your Updated Immiways Portal Password')}&body=${encodeURIComponent(`Dear ${resetCredentials.clientName},\n\nYour portal password has been reset.\n\nLogin URL: ${window.location.origin}/login\nEmail: ${resetCredentials.email}\nNew Password: ${resetCredentials.password}\n\nPlease change your password after logging in.\n\nBest regards,\nImmiways Team`)}`}
                    className="flex-1 px-4 py-2.5 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 transition-all flex items-center justify-center gap-2"
                  >
                    <Mail size={18} />Email Client
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Client Modal */}
        {showEditModal && editingClient && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="edit-client-modal">
            <div className="bg-white rounded-xl w-full max-w-3xl mx-4 shadow-2xl max-h-[90vh] flex flex-col">
              <div className="p-6 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Edit Client</h2>
                  <p className="text-sm text-slate-500 mt-1">Update client details. Changes will sync across all modules.</p>
                </div>
                <button 
                  onClick={() => { setShowEditModal(false); setEditingClient(null); }}
                  className="p-2 hover:bg-slate-100 rounded-lg"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="flex-1 overflow-y-auto p-6">
                <form onSubmit={handleEditSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Personal Information Section */}
                  <div className="md:col-span-2">
                    <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <Users size={20} className="text-teal-600" />
                      Personal Information
                    </h3>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Full Name *</label>
                    <input
                      type="text"
                      value={editFormData.full_name}
                      onChange={(e) => setEditFormData({...editFormData, full_name: e.target.value})}
                      className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Email *</label>
                    <input
                      type="email"
                      value={editFormData.email}
                      onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                      className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Phone *</label>
                    <input
                      type="tel"
                      value={editFormData.phone}
                      onChange={(e) => setEditFormData({...editFormData, phone: e.target.value})}
                      className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Passport Number</label>
                    <input
                      type="text"
                      value={editFormData.passport_number}
                      onChange={(e) => setEditFormData({...editFormData, passport_number: e.target.value})}
                      className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Nationality *</label>
                    <input
                      type="text"
                      value={editFormData.nationality}
                      onChange={(e) => setEditFormData({...editFormData, nationality: e.target.value})}
                      className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Country Applying To *</label>
                    <select
                      value={editFormData.country_applying_to}
                      onChange={(e) => setEditFormData({...editFormData, country_applying_to: e.target.value})}
                      className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                      required
                    >
                      <option value="">Select Country</option>
                      {countries.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Visa Type *</label>
                    <select
                      value={editFormData.visa_type}
                      onChange={(e) => setEditFormData({...editFormData, visa_type: e.target.value})}
                      className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                      required
                    >
                      {visaTypes.map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Sales Consultant *</label>
                    <select
                      value={editFormData.sales_consultant}
                      onChange={(e) => setEditFormData({...editFormData, sales_consultant: e.target.value})}
                      className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                      required
                    >
                      <option value="">Select Sales Consultant</option>
                      {salesConsultants.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  
                  {/* Payment Information Section */}
                  <div className="md:col-span-2 border-t border-slate-200 pt-4 mt-2">
                    <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <Briefcase size={20} className="text-teal-600" />
                      Payment Information
                    </h3>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Service Fee (₹) *</label>
                    <input
                      type="number"
                      value={editFormData.payment_amount}
                      onChange={(e) => setEditFormData({...editFormData, payment_amount: e.target.value})}
                      className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Payment Status *</label>
                    <select
                      value={editFormData.payment_status}
                      onChange={(e) => setEditFormData({...editFormData, payment_status: e.target.value})}
                      className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                      required
                    >
                      <option value="full_paid">Full Amount Paid</option>
                      <option value="partial_paid">Partial Payment</option>
                    </select>
                  </div>
                  {editFormData.payment_status === 'partial_paid' && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Balance Payment Due Date *</label>
                      <input
                        type="date"
                        value={editFormData.balance_due_date}
                        onChange={(e) => setEditFormData({...editFormData, balance_due_date: e.target.value})}
                        className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                        required
                      />
                      <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                        <AlertTriangle size={12} />
                        Reminder emails will be sent 7, 3, and 1 day before the due date
                      </p>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Customer Type</label>
                    <select
                      value={editFormData.customer_type}
                      onChange={(e) => setEditFormData({...editFormData, customer_type: e.target.value})}
                      className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                    >
                      <option value="InHouse">InHouse (Marketing)</option>
                      <option value="B2B">B2B (Partner Referral)</option>
                    </select>
                  </div>
                  
                  {/* Case Assignment Section */}
                  <div className="md:col-span-2 border-t border-slate-200 pt-4 mt-2">
                    <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <UserCheck size={20} className="text-teal-600" />
                      Case Assignment
                    </h3>
                  </div>

                  <div className="md:col-span-2">
                    {caseLoading ? (
                      <div className="flex items-center gap-2 text-slate-500 text-sm py-3">
                        <Loader2 size={16} className="animate-spin" /> Loading case status...
                      </div>
                    ) : (
                      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-4">
                        {/* Case Status Banner */}
                        {!clientCase ? (
                          <div className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full bg-amber-500 inline-block"></span>
                            <span className="text-sm font-medium text-amber-700">No case created yet for this client</span>
                          </div>
                        ) : clientCase.assigned_officer_id ? (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="w-2.5 h-2.5 rounded-full bg-teal-500 inline-block"></span>
                              <span className="text-sm font-medium text-teal-700">
                                Case {clientCase.ucin} — Assigned to{' '}
                                <strong>{officers.find(o => o.id === clientCase.assigned_officer_id)?.full_name || 'Officer'}</strong>
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => navigate(`/admin/cases/${clientCase.id}`)}
                              className="text-xs text-teal-600 hover:underline flex items-center gap-1"
                            >
                              <Eye size={14} /> View Case
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full bg-orange-400 inline-block"></span>
                            <span className="text-sm font-medium text-orange-700">
                              Case {clientCase.ucin} exists — <strong>No officer assigned yet</strong>
                            </span>
                          </div>
                        )}

                        {/* Officer Dropdown */}
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            {!clientCase ? 'Assign Officer & Create Case' : clientCase.assigned_officer_id ? 'Reassign Officer' : 'Assign Officer'}
                          </label>
                          <div className="flex gap-2">
                            <select
                              value={selectedOfficer}
                              onChange={(e) => setSelectedOfficer(e.target.value)}
                              className="flex-1 px-4 py-2 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none text-sm"
                            >
                              <option value="">Select Case Officer</option>
                              {officers.map(o => (
                                <option key={o.id} value={o.id}>{o.full_name}</option>
                              ))}
                            </select>
                            <button
                              type="button"
                              onClick={handleAssignOrCreateCase}
                              disabled={assigningCase || !selectedOfficer}
                              className="px-4 py-2 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 disabled:opacity-50 flex items-center gap-2 text-sm whitespace-nowrap"
                            >
                              {assigningCase ? (
                                <><Loader2 size={15} className="animate-spin" /> Working...</>
                              ) : (
                                <><UserCheck size={15} /> {!clientCase ? 'Create & Assign' : 'Assign & Notify'}</>
                              )}
                            </button>
                          </div>
                          <p className="text-xs text-slate-500 mt-1.5">
                            Officer receives email + notification. Client gets notified in portal.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Sync Notice */}
                  <div className="md:col-span-2 bg-teal-50 border border-teal-200 rounded-lg p-4 mt-2">
                    <p className="text-sm text-teal-800">
                      <strong>Auto-Sync:</strong> Payment changes will automatically sync to:
                    </p>
                    <ul className="text-sm text-teal-700 mt-2 list-disc list-inside">
                      <li>Accounting module records</li>
                      <li>Due payments admin view</li>
                      <li>Client portal payment status</li>
                      <li>Analytics dashboard</li>
                    </ul>
                  </div>
                </form>
              </div>
              
              <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => { setShowEditModal(false); setEditingClient(null); }}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditSubmit}
                  disabled={savingEdit}
                  className="px-6 py-2 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 disabled:opacity-50 flex items-center gap-2"
                  data-testid="save-edit-client-btn"
                >
                  {savingEdit ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={18} />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
